import { DialogComponent } from '@theme/dialog';
import { getScrollContainer } from '@theme/scroll-container';

const sessionKey = 'nervape-newsletter-session-seen';
const dismissedKey = 'nervape-newsletter-dismissed-at';
const subscribedKey = 'nervape-newsletter-subscribed';
let shownThisPage = false;
let selectedEditorSection;

class NewsletterPopup extends DialogComponent {
  #controller;
  #triggers;
  #timer;
  #remainingDelay = 0;
  #visibleSince;
  #subscribed = false;

  #setting(name, fallback) {
    const value = Number(this.dataset[name] ?? fallback);
    return Number.isFinite(value) ? Math.max(0, value) : fallback;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('submit', (event) => {
      if (!event.target.matches('[data-newsletter-form]') || !this.refs.dialog.matches(':modal')) return;
      // Shopify mounts its challenge outside the dialog, where showModal makes it inert.
      this.setAttribute('data-submitting', '');
      this.refs.dialog.close();
      this.refs.dialog.show();
    }, { capture: true, signal });
    this.addEventListener('dialog:close', () => this.removeAttribute('data-submitting'), { signal });
    document.addEventListener('newsletter:success', () => {
      this.#subscribed = true;
      this.#stopWaiting();
      this.closeDialog();
    }, { signal });
    if (this.hasAttribute('data-design-mode')) {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail.sectionId !== this.dataset.sectionId) return;
        selectedEditorSection = this.dataset.sectionId;
        this.showDialog();
      }, { signal });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail.sectionId !== this.dataset.sectionId) return;
        selectedEditorSection = undefined;
        this.closeDialog();
      }, { signal });
      this.addEventListener('dialog:close', () => { selectedEditorSection = undefined; }, { signal });
      // Section setting edits replace the element without selecting the section again.
      if (selectedEditorSection === this.dataset.sectionId) this.showDialog();
      return;
    }
    // Give old "seen" records a finite cooldown, without treating them as subscriptions.
    try {
      if (localStorage.getItem('nervape-newsletter-seen')) {
        if (!localStorage.getItem(dismissedKey)) localStorage.setItem(dismissedKey, String(Date.now()));
        localStorage.removeItem('nervape-newsletter-seen');
      }
    } catch { /* Storage may be blocked. */ }

    this.addEventListener('dialog:close', () => {
      if (this.#subscribed) return;
      try { localStorage.setItem(dismissedKey, String(Date.now())); } catch { /* Storage may be blocked. */ }
    }, { signal });
    if (this.#suppressed()) return;

    this.addEventListener('dialog:open', () => {
      shownThisPage = true;
      try { sessionStorage.setItem(sessionKey, '1'); } catch { /* Storage may be blocked. */ }
      this.#stopWaiting();
    }, { once: true, signal });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.#startWaiting, { once: true, signal });
    } else {
      this.#startWaiting();
    }
  }

  #suppressed() {
    if (shownThisPage || this.#subscribed) return true;
    try {
      if (sessionStorage.getItem(sessionKey)) return true;
    } catch { /* Fall back to the current page when session storage is blocked. */ }
    try {
      if (localStorage.getItem(subscribedKey)) return true;
      const dismissed = Number(localStorage.getItem(dismissedKey));
      return dismissed > 0 && Date.now() - dismissed < this.#setting('cooldownDays', 14) * 86400000;
    } catch { return false; }
  }

  #startWaiting = () => {
    this.#triggers = new AbortController();
    const { signal } = this.#triggers;
    this.#remainingDelay = this.#setting('delaySeconds', 15) * 1000;
    // Capture also receives scroll events from the desktop page wrapper after a viewport change.
    document.addEventListener('scroll', this.#tryOpen, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#tryOpen, { signal });
    window.addEventListener('storage', this.#tryOpen, { signal });
    document.addEventListener('visibilitychange', this.#updateTimer, { signal });
    document.addEventListener('close', (event) => {
      if (!(event.target instanceof HTMLDialogElement) || event.target === this.refs.dialog) return;
      this.#remainingDelay = this.#setting('delaySeconds', 15) * 1000;
      this.#visibleSince = undefined;
      this.#updateTimer();
    }, { capture: true, signal });
    this.#updateTimer();
  };

  #updateTimer = () => {
    clearTimeout(this.#timer);
    if (this.#visibleSince !== undefined) {
      this.#remainingDelay = Math.max(0, this.#remainingDelay - (performance.now() - this.#visibleSince));
    }
    this.#visibleSince = undefined;
    if (document.hidden) return;
    this.#visibleSince = performance.now();
    if (this.#remainingDelay > 0) {
      this.#timer = setTimeout(this.#updateTimer, Math.min(this.#remainingDelay, 2147483647));
    } else {
      this.#tryOpen();
    }
  };

  #tryOpen = () => {
    if (!this.isConnected || this.#suppressed()) {
      this.#stopWaiting();
      return;
    }
    if (document.hidden || this.#remainingDelay > 0) return;
    const container = getScrollContainer();
    const scrollable = container.scrollHeight - container.clientHeight;
    const depth = scrollable > 0 ? container.scrollTop / scrollable * 100 : 100;
    if (depth < Math.min(100, this.#setting('scrollPercent', 30))) return;
    if (document.querySelector('dialog[open]')) return;
    this.#stopWaiting();
    this.showDialog();
  };

  #stopWaiting() {
    clearTimeout(this.#timer);
    this.#visibleSince = undefined;
    this.#triggers?.abort();
  }

  disconnectedCallback() {
    this.#stopWaiting();
    this.#controller?.abort();
    super.disconnectedCallback();
  }
}

if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopup);
